import { type BeamstarSession, StatusVariant } from '@consigli/types';

import { counted, formatCount, workflowName } from '../format';

export interface Problem {
  text: string;
  variant: StatusVariant;
}

/** What went wrong in a session's turns, worst first. */
export const sessionProblems = (session: BeamstarSession): Problem[] => {
  const problems: Problem[] = [];
  if (session.failedTurns > 0) {
    problems.push({
      text: counted(session.failedTurns, 'failed turn', 'failed turns'),
      variant: StatusVariant.ERROR,
    });
  }
  if (session.unfinishedTurns > 0) {
    problems.push({
      text: `${formatCount(session.unfinishedTurns)} never finished`,
      variant: StatusVariant.ERROR,
    });
  }
  if (session.lostTurns > 0) {
    problems.push({
      text: `${formatCount(session.lostTurns)} lost connection`,
      variant: StatusVariant.WARNING,
    });
  }
  if (session.stoppedTurns > 0) {
    problems.push({
      text: `${formatCount(session.stoppedTurns)} stopped`,
      variant: StatusVariant.WARNING,
    });
  }
  if (session.failedToolCalls > 0) {
    problems.push({
      text: counted(session.failedToolCalls, 'failed call', 'failed calls'),
      variant: StatusVariant.INFO,
    });
  }
  return problems;
};

/**
 * The sessions whose building or workflows contain `search`, ignoring case.
 * A flagged turn counts as a problem.
 */
export const filterSessions = (
  sessions: BeamstarSession[],
  search: string,
  onlyWithProblems: boolean,
) => {
  const query = search.trim().toLowerCase();
  return sessions.filter((session) => {
    if (onlyWithProblems && session.flags === 0 && sessionProblems(session).length === 0) {
      return false;
    }
    const searchable = [session.buildingId, ...Object.keys(session.workflows).map(workflowName)];
    return searchable.some((text) => text?.toLowerCase().includes(query));
  });
};
